import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Pencil, Trash2, X, Save, GripVertical, Filter } from 'lucide-react'
import { attributesService } from '@/services/attributesService'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'

const ATTRIBUTE_TYPES = [
  { value: 'select',   labelAr: 'قائمة منسدلة',   labelEn: 'Dropdown' },
  { value: 'checkbox', labelAr: 'خيارات متعددة',  labelEn: 'Checkboxes' },
  { value: 'number',   labelAr: 'رقم',             labelEn: 'Number' },
  { value: 'text',     labelAr: 'نص',              labelEn: 'Text' },
]

const EMPTY_FORM = {
  key: '', labelAr: '', labelEn: '',
  type: 'select', optionsText: '',
  isFilterable: true, isRequired: false,
}

function DialogOverlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:50,
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="animate-in" style={{
        background:'#fff', borderRadius:24, padding:28,
        width:'100%', maxWidth:580,
        boxShadow:'0 32px 64px rgba(0,0,0,0.2)',
        maxHeight:'90vh', overflowY:'auto', position:'relative',
      }}>
        <button onClick={onClose} style={{
          position:'absolute', top:16, left:16,
          width:32, height:32, borderRadius:10,
          background:'#F3F3F3', border:'none', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', color:'#767683',
        }}>
          <X size={16}/>
        </button>
        {children}
      </div>
    </div>
  )
}

export default function ManageFiltersDialog({ category, onClose }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc = useQueryClient()

  const [filterForm, setFilterForm] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const { data: attributes, isLoading } = useQuery({
    queryKey: ['category-attributes', category.id],
    queryFn: () => attributesService.getByCategory(category.id),
  })

  const attrs = attributes || []

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['category-attributes', category.id] })
  }

  const createMut = useMutation({
    mutationFn: (data) => attributesService.create(data),
    onSuccess: () => { toast.success('تم إضافة الفلتر'); invalidate(); setFilterForm(null) },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل إضافة الفلتر'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => attributesService.update(id, data),
    onSuccess: () => { toast.success('تم تحديث الفلتر'); invalidate(); setFilterForm(null) },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل تحديث الفلتر'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => attributesService.delete(id),
    onSuccess: () => { toast.success('تم حذف الفلتر'); invalidate(); setDeleteTarget(null) },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل حذف الفلتر'),
  })

  const openAdd = () => setFilterForm({ mode: 'create', data: { ...EMPTY_FORM } })
  const openEdit = (attr) => setFilterForm({
    mode: 'edit',
    data: {
      key: attr.key,
      labelAr: attr.label?.ar || '',
      labelEn: attr.label?.en || '',
      type: attr.type || 'select',
      optionsText: (attr.options || []).join('\n'),
      isFilterable: attr.isFilterable ?? true,
      isRequired: attr.isRequired ?? false,
    },
    id: attr.id,
  })

  const handleSubmit = () => {
    const f = filterForm.data
    if (!f.key.trim()) { toast.error('يرجى إدخال Key'); return }
    if (!f.labelAr.trim() && !f.labelEn.trim()) { toast.error('يرجى إدخال الاسم'); return }
    if ((f.type === 'select' || f.type === 'checkbox') && !f.optionsText.trim()) {
      toast.error('يرجى إدخال الخيارات'); return
    }

    const payload = {
      key: f.key.trim(),
      label: { ar: f.labelAr.trim(), en: f.labelEn.trim() },
      type: f.type,
      options: (f.type === 'select' || f.type === 'checkbox')
        ? f.optionsText.split('\n').map(s => s.trim()).filter(Boolean)
        : [],
      isFilterable: f.isFilterable,
      isRequired: f.isRequired,
      categoryId: category.id,
    }

    if (filterForm.mode === 'create') {
      createMut.mutate(payload)
    } else {
      updateMut.mutate({ id: filterForm.id, data: payload })
    }
  }

  const isPending = createMut.isPending || updateMut.isPending

  return (
    <DialogOverlay onClose={onClose}>
      <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:18, color:'#1A1C1C', marginBottom:4 }}>
        إدارة الفلاتر
      </h3>
      <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:13, color:'#767683', marginBottom:20 }}>
        {category.name?.[lang] || category.name?.ar || category.name?.en || ''}
      </p>

      {filterForm ? (
        <>
          <h4 style={{ fontFamily:'Cairo, sans-serif', fontWeight:800, fontSize:15, color:'#000666', marginBottom:16 }}>
            {filterForm.mode === 'create' ? 'إضافة فلتر جديد' : 'تعديل الفلتر'}
          </h4>

          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>Key *</label>
                <input value={filterForm.data.key} onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, key: e.target.value } }))}
                  placeholder="مثال: ram" dir="ltr"
                  style={{ width:'100%', borderRadius:10, background:'#E2E2E2', border:'none', padding:'10px 14px', fontSize:14, fontFamily:'Tajawal, sans-serif', outline:'none', boxSizing:'border-box', color:'#1A1C1C' }} />
              </div>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>Type *</label>
                <select value={filterForm.data.type} onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, type: e.target.value } }))}
                  style={{ width:'100%', borderRadius:10, background:'#E2E2E2', border:'none', padding:'10px 14px', fontSize:14, fontFamily:'Cairo, sans-serif', outline:'none', color:'#1A1C1C', cursor:'pointer' }}>
                  {ATTRIBUTE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{lang === 'ar' ? t.labelAr : t.labelEn}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>Label (عربي)</label>
                <input value={filterForm.data.labelAr} onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, labelAr: e.target.value } }))}
                  placeholder="مثال: الذاكرة"
                  style={{ width:'100%', borderRadius:10, background:'#E2E2E2', border:'none', padding:'10px 14px', fontSize:14, fontFamily:'Tajawal, sans-serif', outline:'none', boxSizing:'border-box', color:'#1A1C1C' }} />
              </div>
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>Label (English)</label>
                <input value={filterForm.data.labelEn} onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, labelEn: e.target.value } }))}
                  placeholder="e.g. RAM" dir="ltr"
                  style={{ width:'100%', borderRadius:10, background:'#E2E2E2', border:'none', padding:'10px 14px', fontSize:14, fontFamily:'Tajawal, sans-serif', outline:'none', boxSizing:'border-box', color:'#1A1C1C' }} />
              </div>
            </div>

            {(filterForm.data.type === 'select' || filterForm.data.type === 'checkbox') && (
              <div>
                <label style={{ display:'block', fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#454652', marginBottom:6 }}>
                  الخيارات (خيار واحد في كل سطر)
                </label>
                <textarea value={filterForm.data.optionsText} onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, optionsText: e.target.value } }))}
                  rows={4} dir="ltr" placeholder={"4GB\n8GB\n12GB\n16GB"}
                  style={{ width:'100%', borderRadius:10, background:'#E2E2E2', border:'none', padding:'10px 14px', fontSize:14, fontFamily:'Tajawal, sans-serif', outline:'none', boxSizing:'border-box', color:'#1A1C1C', resize:'vertical' }} />
              </div>
            )}

            <div style={{ display:'flex', gap:24, padding:'4px 0' }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontFamily:'Tajawal, sans-serif', fontSize:14, color:'#454652' }}>
                <input type="checkbox" checked={filterForm.data.isFilterable}
                  onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, isFilterable: e.target.checked } }))}
                  style={{ width:18, height:18, accentColor:'#000666', cursor:'pointer' }} />
                قابل للتصفية
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontFamily:'Tajawal, sans-serif', fontSize:14, color:'#454652' }}>
                <input type="checkbox" checked={filterForm.data.isRequired}
                  onChange={(e) => setFilterForm(p => ({ ...p, data: { ...p.data, isRequired: e.target.checked } }))}
                  style={{ width:18, height:18, accentColor:'#000666', cursor:'pointer' }} />
                إجباري
              </label>
            </div>
          </div>

          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={handleSubmit} disabled={isPending}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#000666', border:'none', cursor:isPending?'not-allowed':'pointer', boxShadow:'0 8px 24px rgba(26,35,126,0.15)', opacity:isPending?0.6:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <Save size={16}/>{isPending ? '...' : (filterForm.mode === 'create' ? 'إضافة' : 'حفظ')}
            </button>
            <button onClick={() => setFilterForm(null)}
              style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}>
              رجوع
            </button>
          </div>
        </>
      ) : (
        <>
          <button onClick={openAdd}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:13, color:'#fff', background:'#000666', border:'none', cursor:'pointer', boxShadow:'0 8px 24px rgba(26,35,126,0.2)', marginBottom:16 }}>
            <Plus size={16}/>إضافة فلتر
          </button>

          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner/></div>
          ) : attrs.length === 0 ? (
            <EmptyState icon={Filter} message="لا توجد فلاتر بعد" action={
              <button onClick={openAdd} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 20px', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#000666', border:'none', cursor:'pointer' }}>
                <Plus size={16}/>إضافة أول فلتر
              </button>
            }/>
          ) : (
            <div className="space-y-2">
              {attrs.map((attr) => {
                const label = attr.label?.[lang] || attr.label?.en || attr.label?.ar || attr.key
                const typeLabel = ATTRIBUTE_TYPES.find(t => t.value === attr.type)
                const typeName = typeLabel ? (lang === 'ar' ? typeLabel.labelAr : typeLabel.labelEn) : attr.type
                return (
                  <div key={attr.id}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', borderRadius:12, border:'1px solid #F3F3F3', background:'#FAFAFA' }}>
                    <GripVertical size={16} style={{ color:'#C4C4C4', flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <span style={{ fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#1A1C1C' }}>{label}</span>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:5, background:'rgba(0,6,102,0.07)', color:'#000666', fontFamily:'Tajawal, sans-serif' }}>{attr.key}</span>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:5, background:'rgba(255,109,0,0.1)', color:'#FF6D00', fontFamily:'Tajawal, sans-serif' }}>{typeName}</span>
                        {attr.isFilterable && (
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:5, background:'rgba(27,109,36,0.1)', color:'#1B6D24', fontFamily:'Tajawal, sans-serif' }}>مصفى</span>
                        )}
                        {attr.isRequired && (
                          <span style={{ fontSize:11, padding:'2px 8px', borderRadius:5, background:'rgba(186,26,26,0.1)', color:'#BA1A1A', fontFamily:'Tajawal, sans-serif' }}>إجباري</span>
                        )}
                      </div>
                      {attr.options?.length > 0 && (
                        <div style={{ display:'flex', gap:4, marginTop:6, flexWrap:'wrap' }}>
                          {attr.options.map((opt, i) => (
                            <span key={i} style={{ fontSize:11, padding:'2px 10px', borderRadius:6, background:'#F3F3F3', color:'#454652', fontFamily:'Tajawal, sans-serif' }}>{opt}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                      <button onClick={() => openEdit(attr)} title="تعديل"
                        style={{ width:30, height:30, borderRadius:8, background:'rgba(0,6,102,0.06)', color:'#000666', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Pencil size={13}/>
                      </button>
                      <button onClick={() => setDeleteTarget(attr)} title="حذف"
                        style={{ width:30, height:30, borderRadius:8, background:'rgba(186,26,26,0.06)', color:'#BA1A1A', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div style={{ position:'fixed', inset:0, zIndex:60, display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }}
          onClick={() => setDeleteTarget(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background:'#fff', borderRadius:24, padding:28, width:'100%', maxWidth:400, boxShadow:'0 32px 64px rgba(0,0,0,0.2)', textAlign:'center' }}>
            <div style={{ width:52, height:52, borderRadius:16, background:'rgba(186,26,26,0.1)', color:'#BA1A1A', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
              <Trash2 size={24}/>
            </div>
            <h3 style={{ fontFamily:'Cairo, sans-serif', fontWeight:900, fontSize:18, color:'#1A1C1C', marginBottom:8 }}>حذف الفلتر</h3>
            <p style={{ fontFamily:'Tajawal, sans-serif', fontSize:14, color:'#767683', marginBottom:20 }}>
              هل تريد حذف فلتر "{deleteTarget.label?.[lang] || deleteTarget.label?.ar || deleteTarget.key}"؟
            </p>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => deleteMut.mutate(deleteTarget.id)} disabled={deleteMut.isPending}
                style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, color:'#fff', background:'#BA1A1A', border:'none', cursor:'pointer', opacity:deleteMut.isPending?0.6:1 }}>
                {deleteMut.isPending ? '...' : 'حذف'}
              </button>
              <button onClick={() => setDeleteTarget(null)}
                style={{ flex:1, padding:'11px 0', borderRadius:12, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:14, background:'#F3F3F3', color:'#454652', border:'none', cursor:'pointer' }}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogOverlay>
  )
}
